import {
  langLocaleMap,
  Language,
  supportedLanguages,
} from '@zeiss/lib-helpers';

import { DEFAULT_LOCALE } from 'src/constants';
import {
  CreateInvitationsRequest,
  InvitationType,
} from 'src/types/rest/managementPortalApi';

interface CreateInvitationRequestValues {
  additionalData: {
    key: string;
    value: string;
  }[];
  email: string;
  firstName?: string;
  lastName?: string;
  locale?: string;
  location?: string;
  targetId: string;
}

export const getSupportedLanguageOptions = (
  displayLocale: string = DEFAULT_LOCALE,
) => {
  const displayNames = new Intl.DisplayNames([displayLocale], {
    languageDisplay: 'standard',
    type: 'language',
  });

  return supportedLanguages
    .map((language) => ({
      key: language,
      label: displayNames.of(language) ?? language,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, displayLocale));
};

const normalizeOptionalFieldValue = (value?: string) => {
  if (!value) return undefined;

  const trimmedValue = value.trim();

  return trimmedValue.length ? trimmedValue : undefined;
};

const normalizeLocaleFieldValue = (value?: string) => {
  const locale = normalizeOptionalFieldValue(value);

  if (!locale) return undefined;

  // The form stores a language code from the select, while the invitations API
  // expects the mapped locale value.
  return langLocaleMap[locale as Language];
};

const normalizeAdditionalData = (
  entries: CreateInvitationRequestValues['additionalData'],
) => {
  const additionalData = entries.reduce(
    (acc: Record<string, string>, { key, value }) => {
      if (!key.trim().length && !value.trim().length) {
        return acc;
      }

      acc[key.trim()] = value.trim();

      return acc;
    },
    {},
  );

  return Object.keys(additionalData).length ? additionalData : undefined;
};

export const buildCreateInvitationsRequest = (
  invitationType: InvitationType,
  values: CreateInvitationRequestValues,
): CreateInvitationsRequest => ({
  invitations: [
    {
      additionalData: normalizeAdditionalData(values.additionalData),
      email: values.email.trim(),
      firstName: normalizeOptionalFieldValue(values.firstName),
      lastName: normalizeOptionalFieldValue(values.lastName),
      locale: normalizeLocaleFieldValue(values.locale),
      location: normalizeOptionalFieldValue(values.location),
      ...(invitationType === InvitationType.application
        ? { applicationId: values.targetId }
        : { tenantId: values.targetId }),
    },
  ],
});
